import React, { useState } from 'react';
import { Stack, Typography, Chip, FormControl, InputLabel, Select, OutlinedInput, MenuItem, Checkbox, ListItemText, CircularProgress } from '@mui/material';
import { useGetHospitalsInOrganizationQuery } from '../../../../api/organizationSlice';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_SELECTED_DISPLAY = 1;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 'auto',
            minWidth: 300,
        },
    },
};

function HospitalMultiSelect({ organizationId, onHospitalChange }) {
    const [selectedHospitals, setSelectedHospitals] = useState([]);
    
    const { 
        data: hospitalsResponse, 
        isLoading, 
        error 
    } = useGetHospitalsInOrganizationQuery(organizationId, {
        skip: !organizationId
    });

    // Handle different possible response structures
    const hospitals = hospitalsResponse?.data || hospitalsResponse || [];

    const handleChange = (event) => {
        const value = event.target.value;
        const newSelection = typeof value === 'string' ? value.split(',') : value;
        setSelectedHospitals(newSelection);
        // Don't call onHospitalChange here - wait for dropdown close
    };

    const handleClose = () => {
        onHospitalChange(selectedHospitals);
    };

    if (isLoading) {
        return (
            <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Filter by Location
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Loading hospitals...</InputLabel>
                    <Select
                        multiple
                        disabled
                        value={[]}
                        input={<OutlinedInput label="Loading hospitals..." />}
                        endAdornment={<CircularProgress size={20} />}
                    />
                </FormControl>
            </Stack>
        );
    }

    if (error || !hospitals.length) {
        const errorMessage = error 
            ? `Error loading hospitals: ${error.message || 'Unknown error'}`
            : 'No hospitals available';
            
        return (
            <Stack spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Filter by Location
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{errorMessage}</InputLabel>
                    <Select
                        multiple
                        disabled
                        value={[]}
                        input={<OutlinedInput label={errorMessage} />}
                    />
                </FormControl>
                {error && (
                    <Typography variant="caption" color="error">
                        Debug: {JSON.stringify(error, null, 2)}
                    </Typography>
                )}
            </Stack>
        );
    }

    return (
        <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Filter by Location
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Select Locations</InputLabel>
                <Select
                    multiple
                    value={selectedHospitals}
                    onChange={handleChange}
                    onClose={handleClose}
                    input={<OutlinedInput label="Select Locations" />}
                    renderValue={(selected) => (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {selected.slice(0, MAX_SELECTED_DISPLAY).map((hospitalId) => {
                                const hospital = hospitals.find(h => h.id === hospitalId);
                                return (
                                    <Chip
                                        key={hospitalId}
                                        label={hospital?.name || 'Unknown'}
                                        size="small"
                                        variant="outlined"
                                    />
                                );
                            })}
                            {selected.length > MAX_SELECTED_DISPLAY && (
                                <Chip
                                    label={`+${selected.length - MAX_SELECTED_DISPLAY} more`}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                />
                            )}
                        </div>
                    )}
                    MenuProps={MenuProps}
                >
                    {hospitals.map((hospital) => (
                        <MenuItem key={hospital.id} value={hospital.id}>
                            <Checkbox checked={selectedHospitals.indexOf(hospital.id) > -1} />
                            <ListItemText primary={hospital.name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Stack>
    );
}

export default HospitalMultiSelect;